import MuiTable from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import MuiTableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import MuiTableHead from '@mui/material/TableHead';
import MuiTableRow, {
  TableRowProps as MuiTableRowProps,
} from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import { SxProps } from '@mui/material/styles';
import React from 'react';
import { Checkbox } from 'src/components/Checkbox';
import { Typography } from 'src/components/Typography';

/**
 * This file contains lower-level dumb UI components for building tables
 * For most in-app use cases use DataTable instead
 */

type Row = Record<string, any>;

interface TableProps {
  children: React.ReactNode;
}

export const TableContent: React.FC<TableProps> = ({ children }) => {
  return (
    <TableContainer component="div" sx={{ containerType: 'inline-size' }}>
      <MuiTable component="div">{children}</MuiTable>
    </TableContainer>
  );
};

export const TableColLabels = ({ children, sx, ...props }) => {
  return (
    <MuiTableHead component="div" sx={sx} {...props}>
      <MuiTableRow
        component="div"
        sx={{ backgroundColor: 'var(--mui-palette-background-level1)' }}
      >
        {children}
      </MuiTableRow>
    </MuiTableHead>
  );
};

export const TableRows = ({ ...props }) => (
  <TableBody component="div" {...props} />
);

interface TableRowProps extends MuiTableRowProps {
  children: React.ReactNode;
  selected?: boolean;
  hover?: boolean;
  component?: any;
  to?: string; // Location to navigate when using component={Link}
  sx?: SxProps;
}
export const TableRow: React.FC<TableRowProps> = ({
  selected,
  component = 'div',
  hover,
  to,
  children,
  sx,
  ...props
}) => {
  return (
    <MuiTableRow
      selected={selected}
      component={component}
      hover={hover}
      sx={[{ bgcolor: 'background.paper' }, ...(Array.isArray(sx) ? sx : [sx])]}
      to={to}
      {...props}
    >
      {children}
    </MuiTableRow>
  );
};

interface TableCellProps {
  children?: React.ReactNode;
  actions?: boolean;
  fixed?: boolean;
  formatValue?: (value: any, row: Row) => React.ReactNode;
  row: Row;
  value: any;
  maxWidth?: string;
  minWidth?: string;
  align?: 'left' | 'right' | 'center';
}

export const TableCell: React.FC<TableCellProps> = ({
  actions,
  children,
  value,
  formatValue,
  row,
  fixed,
  maxWidth,
  minWidth,
  align,
}) => {
  return (
    <MuiTableCell
      component="div"
      align={align ?? (actions ? 'right' : 'left')}
      onClick={(e) => {
        if (actions) {
          e.preventDefault();
          e.stopPropagation();
        }
      }}
      sx={[
        {
          maxWidth,
          minWidth,
        },
        !!fixed && {
          position: 'sticky',
          right: '0px',
          backgroundImage:
            'linear-gradient(to right,  rgba(255,255,255,0.0) 0%, var(--mui-palette-background-paper) 20%)',
        },
      ]}
    >
      <Typography sx={{ width: '100%' }} component="div" noWrap>
        {children ? (
          children
        ) : (
          <TableValueDisplay
            row={row}
            value={value}
            formatValue={formatValue}
          />
        )}
      </Typography>
    </MuiTableCell>
  );
};

interface TableValueDisplayProps {
  formatValue?: (value: any, row: Row) => React.ReactNode;
  row: Row;
  value: any;
}
export const TableValueDisplay: React.FC<TableValueDisplayProps> = ({
  value,
  formatValue,
  row,
}) => {
  return formatValue ? formatValue(value, row) : value;
};

interface TableRowSelectionCellProps {
  selected: boolean;
  onChange: () => void;
  indeterminate?: boolean;
  disabled?: boolean;
}
export const TableRowSelectionCell: React.FC<TableRowSelectionCellProps> = ({
  selected,
  indeterminate,
  disabled,
  onChange,
}) => {
  return (
    <MuiTableCell
      component="div"
      align="left"
      sx={{ width: '0px', minWidth: 'none', pr: 1 }}
      onClick={(e) => e.stopPropagation()}
    >
      <Checkbox
        indeterminate={indeterminate}
        checked={selected}
        disabled={disabled}
        onChange={onChange}
      ></Checkbox>
    </MuiTableCell>
  );
};

interface TableColLabelProps {
  children: React.ReactNode;
  sortable?: boolean;
  actions?: boolean;
  fixed?: boolean;
  sorting?: 'asc' | 'desc';
  onClick?: () => void;
  align?: 'left' | 'right' | 'center';
}
export const TableColLabel: React.FC<TableColLabelProps> = ({
  children,
  actions,
  sortable,
  sorting,
  onClick,
  fixed,
  align,
  ...props
}) => {
  return (
    <MuiTableCell
      component="div"
      align={align ?? (actions ? 'right' : 'left')}
      sx={{
        '&&': fixed
          ? {
              position: 'sticky',
              right: '0px',
              backgroundColor: 'transparent',
              backgroundImage:
                'linear-gradient(to right,  transparent 0%, var(--mui-palette-background-level1) 20%)',
            }
          : {},
      }}
      {...props}
    >
      {sortable && (
        <TableSortLabel
          active={!!sorting}
          direction={sorting}
          onClick={onClick}
        >
          <Typography variant="subtitle2" noWrap>
            {children}
          </Typography>
        </TableSortLabel>
      )}
      {!sortable && (
        <Typography variant="subtitle2" noWrap>
          {children}
        </Typography>
      )}
    </MuiTableCell>
  );
};
